import { useTranslation } from 'react-i18next'

interface OnboardingStepBarProps {
  currentStep: number
  totalSteps?: number
  /**
   * When false, render a compact label-less segmented bar (used at the BOTTOM
   * of the wizard, above the footer buttons — Figma 86-4526). Defaults to the
   * full labelled bar.
   */
  showLabels?: boolean
  /**
   * When provided, segments up to (and including) the current step become
   * clickable to navigate back to that step. Later steps stay inert (forward
   * navigation is via the footer's Next). Honoured by BOTH variants.
   *
   * It used to be minimal-variant only, which silently cost desktop its
   * click-to-go-back: before EP-114 the minimal bar rendered at every
   * breakpoint, so desktop got it for free. T003 moved desktop to the labelled
   * bar, which had no click wiring, and gating the minimal bar to `md:hidden`
   * completed the regression. Restored 2026-08-04.
   */
  onStepClick?: (step: number) => void
  /**
   * Explicit step labels, one per step. Defaults to the WIZARD's own i18n keys
   * (`step_bar.labels.N`).
   *
   * Added for EP-115 C-3 so the data-import flow can adopt this component instead
   * of the numbered-circle indicator it had duplicated verbatim in both
   * ImportWizardPage and ImportDetailPage. That flow has FIVE steps of its own
   * (Upload / Parsing / Review / Import / Done), so it cannot reuse the wizard's
   * eight label keys. Additive: omit it and nothing changes.
   */
  labels?: string[]
  /**
   * EP-158 AC-2: the furthest step ever reached in this wizard session. When
   * provided, clickability widens from "step <= currentStep" (back only) to
   * "step <= reachedStep" (forward or back, up to wherever the user has
   * already been), and a reached-but-not-current segment renders with its own
   * fill (lighter than the active/current segment) instead of the plain
   * completed/idle split.
   *
   * Omit it and nothing changes — the data-import flow (ImportDetailPage)
   * reuses this component with no reachedStep and keeps its original
   * back-only behaviour.
   */
  reachedStep?: number
}

export function OnboardingStepBar({
  currentStep,
  totalSteps = 8,
  showLabels = true,
  onStepClick,
  labels,
  reachedStep,
}: OnboardingStepBarProps) {
  const { t } = useTranslation('onboarding')

  // Minimal segmented progress bar (no labels) — wizard footer variant.
  if (!showLabels) {
    return (
      <div
        className="flex items-center gap-[2px] w-full"
        role="progressbar"
        aria-valuenow={currentStep}
        aria-valuemin={1}
        aria-valuemax={totalSteps}
        aria-label={t('step_bar.aria_label')}
        data-testid="step-bar-minimal"
      >
        {Array.from({ length: totalSteps }, (_, i) => {
          const step = i + 1
          const filled = reachedStep !== undefined ? step <= reachedStep : step <= currentStep
          const segClass = `flex-1 h-1.5 rounded-full transition-colors ${filled ? 'bg-primary' : 'bg-base-300'}`
          // Only completed/earlier steps are clickable (backward nav), unless
          // reachedStep widens that to anywhere already visited (AC-2).
          const clickable = onStepClick && (reachedStep !== undefined ? step <= reachedStep : step <= currentStep)
          return clickable ? (
            <button
              key={step}
              type="button"
              onClick={() => onStepClick(step)}
              aria-label={t('step_bar.go_to_step', { n: step })}
              className={`${segClass} cursor-pointer hover:opacity-70`}
              data-testid={`step-bar-segment-${step}`}
            />
          ) : (
            <div key={step} className={segClass} data-testid={`step-bar-segment-${step}`} />
          )
        })}
      </div>
    )
  }

  return (
    <>
      {/* Desktop step bar — 8 labelled segments, hidden on mobile */}
      {/* gap-1 (4px) between steps. Taken from Figma 183:2754, where the
          segments are 179.5px wide and start 183.5px apart. Without it the
          segments butt together into one continuous rule. */}
      <nav
        aria-label={t('step_bar.aria_label')}
        className="hidden md:flex items-center w-full gap-1"
        data-testid="step-bar-desktop"
      >
        {Array.from({ length: totalSteps }, (_, i) => {
          const step = i + 1
          const isActive = step === currentStep
          const isCompleted = step < currentStep
          const hasReachedTracking = reachedStep !== undefined
          const isReachedNotActive = hasReachedTracking && !isActive && step <= reachedStep!

          // Figma has only two visual states for the segment fill — completed
          // and active both render filled (bg-primary); only idle is the track
          // colour. The label, however, has exactly two states of its own:
          // active (#004C50 / text-primary) vs default (#6B6561 / text-base-content,
          // full opacity) — completed and idle must look IDENTICAL there.
          //
          // EP-158 AC-2: once reachedStep is tracked, a THIRD fill state
          // appears — a reached-but-not-current segment (forward OR backward
          // of currentStep) renders a lighter fill than the solid active
          // segment, instead of folding into the old completed/idle split.
          // Without reachedStep (e.g. the import flow) this stays exactly the
          // original two-state logic.
          const isFilled = isActive || (hasReachedTracking ? isReachedNotActive : isCompleted)
          const fillClass = isActive ? 'bg-primary' : isReachedNotActive ? 'bg-primary/50' : isFilled ? 'bg-primary' : 'bg-base-300'
          // Same rule as the minimal variant: only completed/current steps are
          // clickable (backward navigation only), unless reachedStep widens
          // that to anywhere already visited, forward or back (AC-2). Forward
          // otherwise stays with the footer's Next, which is what runs each
          // step's save.
          const clickable = Boolean(onStepClick) && (hasReachedTracking ? step <= reachedStep! : step <= currentStep)

          const column = (
            <>
              {/* Segment bar. SIZE reverted to the pre-EP-114 h-1.5 (owner,
                  2026-08-04); the rounded-[64px] radius and the fill logic
                  above are T003's and stay. */}
              <div
                className={`w-full h-1.5 rounded-[64px] transition-colors ${fillClass}`}
              />
              {/* Step label. SIZE reverted to the pre-EP-114 `text-xs
                  font-medium` with no vertical padding (owner, 2026-08-04).
                  T003 had grown this to `py-3.5 text-base font-semibold`, which
                  alone accounted for 52px of the bar's 81px. The two-state
                  COLOUR logic is T003's Figma-correctness fix and is kept:
                  active vs default, with completed reading identical to idle. */}
              <span
                className={`text-xs font-medium select-none ${
                  isActive ? 'text-primary' : 'text-base-content'
                }`}
              >
                {labels ? labels[step - 1] : t(`step_bar.labels.${step}`)}
              </span>
            </>
          )

          // The whole column is the hit target, not just the 6px bar, so the
          // label is clickable too.
          const columnClass = 'flex-1 flex flex-col items-center gap-1'

          return clickable ? (
            <button
              key={step}
              type="button"
              onClick={() => onStepClick!(step)}
              aria-label={t('step_bar.go_to_step', { n: step })}
              aria-current={isActive ? 'step' : undefined}
              className={`${columnClass} cursor-pointer hover:opacity-70`}
              data-testid={`step-bar-desktop-segment-${step}`}
            >
              {column}
            </button>
          ) : (
            <div
              key={step}
              className={columnClass}
              aria-current={isActive ? 'step' : undefined}
              data-testid={`step-bar-desktop-segment-${step}`}
            >
              {column}
            </div>
          )
        })}
      </nav>

      {/* Mobile compact step bar — "Step N of 8" + dots, visible only on mobile */}
      <div
        className="md:hidden flex flex-col items-center gap-2"
        data-testid="step-bar-mobile"
      >
        <p className="text-sm font-medium text-base-content">
          {t('step_bar.step_of', { n: currentStep, total: totalSteps })}
        </p>
        <div className="flex items-center gap-1.5" role="list" aria-label={t('step_bar.indicators_aria_label')}>
          {Array.from({ length: totalSteps }, (_, i) => {
            const step = i + 1
            const isActive = step === currentStep
            const isCompleted = step < currentStep

            return (
              <div
                key={step}
                role="listitem"
                aria-label={
                  isActive
                    ? t('step_bar.step_aria_label.current', { n: step })
                    : isCompleted
                      ? t('step_bar.step_aria_label.completed', { n: step })
                      : t('step_bar.step_aria_label.idle', { n: step })
                }
                className={`rounded-full transition-all ${
                  isActive
                    ? 'w-3 h-3 bg-primary'
                    : isCompleted
                      ? 'w-2 h-2 bg-primary/60'
                      : 'w-2 h-2 bg-base-300'
                }`}
              />
            )
          })}
        </div>
      </div>
    </>
  )
}
